
import { ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAdministrationAPI } from "../AdministrationAPI";

import Section from "../components/Section";

import { useUrlPageState } from "../utilities/dataTablePagination";
import { useModalContext } from "../components/ModalProvider";

import type { ColumnDef } from "@tanstack/react-table";
import { Pencil, Trash2, UserPlus } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

enum DialogMode {
    create = 'create',
    update = 'update',
}

/** Leerzustand einer Tabelle: Überschrift, Erklärung und Handlungsaufforderung. */
function TableEmptyState({
    title,
    hint,
    action,
}: {
    title: string;
    hint?: string;
    action?: ReactNode;
}) {
    return (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
            {action}
        </div>
    );
}

/**
 * Pflichtfeld im Dialog. Die abgelöste `TextField`-API prüfte über
 * `onGetErrorMessage` + `validateOnFocusOut`, blockierte das Speichern aber
 * nie — dieselbe Semantik: Meldung erscheint nach dem Verlassen des Feldes,
 * die Schaltfläche bleibt bedienbar.
 */
function RequiredField({
    id,
    label,
    value,
    type,
    errorMessage,
    onChange,
}: {
    id: string;
    label: string;
    value: string | undefined;
    type?: string;
    errorMessage: string;
    onChange: (value: string) => void;
}) {
    const [touched, setTouched] = useState(false);
    const invalid = touched && !(value && value.length > 0);
    const errorId = `${id}-error`;

    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={id}>
                {label}
                <span aria-hidden className="ml-0.5 text-destructive">*</span>
            </Label>
            <Input
                id={id}
                type={type}
                required
                value={value || ''}
                aria-invalid={invalid || undefined}
                aria-describedby={invalid ? errorId : undefined}
                onBlur={() => setTouched(true)}
                onChange={(event) => onChange(event.target.value)}
            />
            {invalid ? (
                <p id={errorId} className="text-sm text-destructive">
                    {errorMessage}
                </p>
            ) : null}
        </div>
    );
}

export default function CustomerAndUserManagementPanel(_properties: any) {
    const { t } = useTranslation();
    const fieldId = useId();
    const { showDeleteDialog } = useModalContext() as any;
    const { getCustomers, createCustomer, updateCustomer, deleteCustomer, getUsers, createUser, updateUser, deleteUser } = useAdministrationAPI();
    const customersPagination = useUrlPageState('customersPage');
    const usersPagination = useUrlPageState('usersPage');
    const [customerFilter, setCustomerFilter] = useState() as any;
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(true);
    const [customer, setCustomer] = useState() as any;
    const [userFilter, setUserFilter] = useState() as any;
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [user, _setUser] = useState() as any;
    const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
    const [customerDialogMode, setCustomerDialogMode] = useState(DialogMode.create);
    const [editedCustomer, setEditedCustomer] = useState() as any;
    const [userDialogOpen, setUserDialogOpen] = useState(false);
    const [userDialogMode, setUserDialogMode] = useState(DialogMode.create);
    const [editedUser, setEditedUser] = useState() as any;

    const customerFilterId = `${fieldId}-customer-filter`;
    const userFilterId = `${fieldId}-user-filter`;
    const customerNameId = `${fieldId}-customer-name`;
    const customerDomainId = `${fieldId}-customer-domain`;

    const filterItem = (filter: string, item: any, fields: any) => {
        if (!filter || filter.trim().length === 0) {
            return true;
        }

        for (const field of fields) {
            if (item[field] &&
                item[field].toLowerCase().indexOf(filter.toLowerCase()) === 0) {
                return true;
            }
        }

        return false;
    };

    const filterCustomers = async () => {
        setCustomersLoading(true);
        try {
            const allCustomers = await getCustomers();

            setCustomers(allCustomers.filter((customer: any) => filterItem(customerFilter, customer, ['name', 'domain'])));
        } finally {
            setCustomersLoading(false);
        }
    };

    const filterUsers = async () => {
        if (!customer) {
            return;
        }

        setUsersLoading(true);
        try {
            const allUsers = await getUsers(customer.id);

            setUsers(allUsers.filter((user: any) => filterItem(userFilter, user, ['firstName', 'lastName'])));
        } finally {
            setUsersLoading(false);
        }
    };

    useEffect(() => {
        filterCustomers();
    }, [customerFilter]);

    useEffect(() => {
        filterUsers();
    }, [userFilter, customer]);

    const editCustomer = (customer: any) => {
        setEditedCustomer({ ...customer });
        toggleCustomerDialogOpen();
    }

    const toggleCustomerDialogOpen = () => {
        setCustomerDialogOpen(!customerDialogOpen);
    }

    const editUser = (user: any) => {
        setEditedUser({ ...user });
        toggleUserDialogOpen();
    }

    const toggleUserDialogOpen = () => {
        setUserDialogOpen(!userDialogOpen);
    }

    const customerColumns: ColumnDef<any>[] = [{
        id: 'id',
        size: 150,
        enableSorting: false,
        header: () => 'ID',
        cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original.id}</span>,
    }, {
        id: 'name',
        size: 200,
        enableSorting: false,
        header: () => 'Name',
        cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original.name}</span>,
    }, {
        id: 'created',
        size: 250,
        enableSorting: false,
        header: () => <div className="text-right">Created</div>,
        cell: ({ row }) => (
            <div className="whitespace-nowrap text-right text-sm">
                {new Date(row.original.created).toLocaleDateString()} {new Date(row.original.created).toLocaleTimeString()}
            </div>
        ),
    }, {
        id: 'domain',
        size: 200,
        enableSorting: false,
        header: () => 'Domain',
        cell: ({ row }) => (
            <a
                className="min-w-0 break-all text-sm underline underline-offset-2"
                href={`https://${row.original.domain}`}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
            >
                {row.original.domain}
            </a>
        ),
    }, {
        id: 'actions',
        size: 120,
        enableSorting: false,
        header: () => 'Actions',
        cell: ({ row }) => (
            <div className="flex gap-1">
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`${t('global.edit')}: ${row.original.name}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        setCustomerDialogMode(DialogMode.update);
                        editCustomer(row.original);
                    }}
                >
                    <Pencil aria-hidden className="h-4 w-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`${t('global.delete')}: ${row.original.name}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        showDeleteDialog('Delete Customer', row.original.name, async () => {
                            setCustomer(null);
                            await deleteCustomer(row.original.id);

                            filterCustomers();
                        });
                    }}
                >
                    <Trash2 aria-hidden className="h-4 w-4" />
                </Button>
            </div>
        ),
    }];

    const userColumns: ColumnDef<any>[] = [
        {
            id: 'user',
            size: 300,
            enableSorting: false,
            header: () => 'User',
            cell: ({ row }) => (
                <div className="flex min-w-0 items-center gap-3">
                    <Avatar>
                        <AvatarFallback>
                            {`${row.original.firstName.toUpperCase().charAt(0)}${row.original.lastName.toUpperCase().charAt(0)}`}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                        <div className="min-w-0 break-words text-sm font-medium">
                            {row.original.firstName} {row.original.lastName}
                        </div>
                        <div className="min-w-0 break-all text-sm text-muted-foreground">
                            {row.original.email}
                        </div>
                    </div>
                </div>
            ),
        }, {
            id: 'status',
            size: 100,
            enableSorting: false,
            header: () => 'Status',
            cell: () => null,
        }, {
            id: 'administrator',
            size: 100,
            enableSorting: false,
            header: () => <div className="text-center">Administrator</div>,
            cell: ({ row }) => (
                <div className="flex justify-center">
                    <Checkbox
                        checked={row.original.roles && row.original.roles.indexOf('administrator') >= 0}
                        aria-label={t('administrationPage.customerManagementPanel.administratorFor', {
                            name: `${row.original.firstName} ${row.original.lastName}`,
                        })}
                        onCheckedChange={async (checked) => {
                            const editedRow = row.original;

                            editedRow.roles = editedRow.roles || [];

                            if (checked === true && editedRow.roles.indexOf('administrator') < 0) {
                                editedRow.roles.push('administrator')
                            } else {
                                editedRow.roles = editedRow.roles.filter((role: string) => role !== 'administrator')
                            }

                            await updateUser(customer.id, editedRow);
                        }}
                    />
                </div>
            ),
        }, {
            id: 'actions',
            size: 100,
            enableSorting: false,
            header: () => 'Actions',
            cell: ({ row }) => (
                <div className="flex gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`${t('global.edit')}: ${row.original.firstName} ${row.original.lastName}`}
                        onClick={() => {
                            editUser(row.original);
                            setUserDialogMode(DialogMode.update);
                        }}
                    >
                        <Pencil aria-hidden className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`${t('global.delete')}: ${row.original.firstName} ${row.original.lastName}`}
                        onClick={() => showDeleteDialog('Delete User', `${row.original.firstName} ${row.original.lastName}`, async () => {
                            await deleteUser(customer.id, row.original);

                            filterUsers();
                        })}
                    >
                        <Trash2 aria-hidden className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ];

    const customerFilterActive = Boolean(customerFilter && String(customerFilter).trim().length > 0);
    const userFilterActive = Boolean(userFilter && String(userFilter).trim().length > 0);

    return <>
        <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-6">
            <Section title="Customers">
                <div className="flex min-w-0 flex-wrap items-end gap-4">
                    <div className="flex w-[200px] min-w-0 max-w-full flex-col gap-1.5">
                        <Label htmlFor={customerFilterId}>Filter</Label>
                        <Input
                            id={customerFilterId}
                            value={customerFilter || ''}
                            onChange={(event) => setCustomerFilter(event.target.value)}
                        />
                    </div>
                    <Button
                        variant="outline"
                        className="max-sm:w-full"
                        onClick={() => {
                            setCustomerDialogMode(DialogMode.create);
                            editCustomer({});
                        }}
                    >
                        <UserPlus aria-hidden className="mr-2 h-4 w-4" />
                        Create customer
                    </Button>
                </div>
                <div className="mt-4 min-w-0 overflow-x-auto">
                    <DataTable
                        columns={customerColumns}
                        data={customers}
                        loading={customersLoading}
                        pageSize={5}
                        page={customersPagination.page}
                        onPageChange={customersPagination.onPageChange}
                        onRowClick={(row) => setCustomer(row)}
                        rowClassName={(row: any) =>
                            customer && row.id === customer.id ? 'bg-accent' : undefined
                        }
                        previousAriaLabel={t('global.back')}
                        nextAriaLabel={t('global.next')}
                        emptyMessage={
                            customerFilterActive ? (
                                <TableEmptyState
                                    title={t('administrationPage.customerManagementPanel.noMatchingCustomersTitle')}
                                    hint={t('administrationPage.customerManagementPanel.noMatchingCustomersHint')}
                                    action={
                                        <Button variant="outline" size="sm" onClick={() => setCustomerFilter('')}>
                                            {t('billingPanel.resetFilter')}
                                        </Button>
                                    }
                                />
                            ) : (
                                <TableEmptyState
                                    title={t('administrationPage.customerManagementPanel.noCustomersTitle')}
                                    hint={t('administrationPage.customerManagementPanel.noCustomersHint')}
                                />
                            )
                        }
                    />
                </div>
            </Section>
            <div className="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <Section title="Users">
                    {customer
                        ?
                        <>
                            <div className="flex min-w-0 flex-wrap items-end gap-4">
                                <div className="flex w-[200px] min-w-0 max-w-full flex-col gap-1.5">
                                    <Label htmlFor={userFilterId}>Filter</Label>
                                    <Input
                                        id={userFilterId}
                                        value={userFilter || ''}
                                        onChange={(event) => setUserFilter(event.target.value)}
                                    />
                                </div>
                                <Button
                                    variant="outline"
                                    className="max-sm:w-full"
                                    onClick={() => {
                                        setUserDialogMode(DialogMode.create);
                                        editUser({});
                                    }}
                                >
                                    <UserPlus aria-hidden className="mr-2 h-4 w-4" />
                                    Create user
                                </Button>
                            </div>
                            <div className="mt-4 min-w-0 overflow-x-auto">
                                <DataTable
                                    columns={userColumns}
                                    data={users}
                                    loading={usersLoading}
                                    pageSize={5}
                                    page={usersPagination.page}
                                    onPageChange={usersPagination.onPageChange}
                                    previousAriaLabel={t('global.back')}
                                    nextAriaLabel={t('global.next')}
                                    emptyMessage={
                                        userFilterActive ? (
                                            <TableEmptyState
                                                title={t('administrationPage.customerManagementPanel.noMatchingUsersTitle')}
                                                hint={t('administrationPage.customerManagementPanel.noMatchingUsersHint')}
                                                action={
                                                    <Button variant="outline" size="sm" onClick={() => setUserFilter('')}>
                                                        {t('billingPanel.resetFilter')}
                                                    </Button>
                                                }
                                            />
                                        ) : (
                                            <TableEmptyState
                                                title={t('administrationPage.customerManagementPanel.noUsersTitle')}
                                                hint={t('administrationPage.customerManagementPanel.noUsersHint')}
                                            />
                                        )
                                    }
                                />
                            </div>
                        </>
                        :
                        <div className="flex flex-col items-center gap-2 py-6 text-center">
                            <div className="text-sm font-semibold text-foreground">No customer selected.</div>
                            <div className="max-w-prose text-sm text-muted-foreground">
                                {t('administrationPage.customerManagementPanel.selectCustomerHint')}
                            </div>
                        </div>
                    }
                </Section>
                <Section title="Authentication & SSO">
                    <p className="text-sm text-muted-foreground">
                        {t('administrationPage.customerManagementPanel.ssoPlaceholder')}
                    </p>
                </Section>
            </div>
        </div>
        {editedCustomer ?
            <Dialog
                open={customerDialogOpen}
                onOpenChange={(open) => { if (!open) setCustomerDialogOpen(false); }}
            >
                <DialogContent
                    className="w-[calc(100vw-2rem)] sm:max-w-[600px]"
                    onInteractOutside={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>
                            {customerDialogMode === DialogMode.update ? 'Edit Customer' : 'Create Customer'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex min-w-0 flex-col gap-4">
                        <div className="flex min-w-0 flex-col gap-1.5">
                            <Label htmlFor={customerNameId}>Name</Label>
                            <Input
                                id={customerNameId}
                                value={editedCustomer.name || ''}
                                onChange={(event) => setEditedCustomer({ ...editedCustomer, name: event.target.value })}
                            />
                        </div>
                        <div className="flex min-w-0 flex-col gap-1.5">
                            <Label htmlFor={customerDomainId}>Domain</Label>
                            <Input
                                id={customerDomainId}
                                value={editedCustomer.domain || ''}
                                onChange={(event) => setEditedCustomer({ ...editedCustomer, domain: event.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            className="max-sm:w-full"
                            onClick={toggleCustomerDialogOpen}
                        >
                            {t('global.cancel')}
                        </Button>
                        <Button
                            className="max-sm:w-full"
                            onClick={async () => {
                                if (customerDialogMode === DialogMode.update) {
                                    updateCustomer({ ...customer, ...editedCustomer });
                                    filterCustomers();
                                } else {
                                    await createCustomer(editedCustomer);
                                    filterCustomers();
                                }

                                toggleCustomerDialogOpen();
                            }}
                        >
                            {customerDialogMode === DialogMode.update ? 'Apply' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            :
            <></>
        }
        {
            editedUser ?
                <Dialog
                    open={userDialogOpen}
                    onOpenChange={(open) => { if (!open) setUserDialogOpen(false); }}
                >
                    <DialogContent
                        className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[600px]"
                        onInteractOutside={(event) => event.preventDefault()}
                    >
                        <DialogHeader>
                            <DialogTitle>
                                {userDialogMode === DialogMode.update ? 'Edit User' : 'Create User'}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="flex min-w-0 flex-col gap-4">
                            <RequiredField
                                id={`${fieldId}-user-email`}
                                label="E-Mail"
                                value={editedUser.email}
                                errorMessage="Email cannot be empty."
                                onChange={(email) => setEditedUser({ ...editedUser, email })}
                            />
                            <RequiredField
                                id={`${fieldId}-user-first-name`}
                                label="First Name"
                                value={editedUser.firstName}
                                errorMessage="First name cannot be empty."
                                onChange={(firstName) => setEditedUser({ ...editedUser, firstName })}
                            />
                            <RequiredField
                                id={`${fieldId}-user-last-name`}
                                label="Last Name"
                                value={editedUser.lastName}
                                errorMessage="Last name cannot be empty."
                                onChange={(lastName) => setEditedUser({ ...editedUser, lastName })}
                            />
                            <RequiredField
                                id={`${fieldId}-user-password`}
                                label="Password"
                                type="password"
                                value={editedUser.password}
                                errorMessage="Password cannot be empty."
                                onChange={(password) => setEditedUser({ ...editedUser, password })}
                            />
                            <RequiredField
                                id={`${fieldId}-user-repeat-password`}
                                label="Repeat password"
                                type="password"
                                value={editedUser.repeatPassword}
                                errorMessage="Repeat password cannot be empty."
                                onChange={(repeatPassword) => setEditedUser({ ...editedUser, repeatPassword })}
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                className="max-sm:w-full"
                                onClick={toggleUserDialogOpen}
                            >
                                {t('global.cancel')}
                            </Button>
                            <Button
                                className="max-sm:w-full"
                                onClick={async () => {
                                    if (userDialogMode === DialogMode.update) {
                                        await updateUser(customer.id, { ...user, ...editedUser });

                                        filterUsers();
                                    } else {
                                        await createUser(customer.id, { ...editedUser, oneTimePassword: true });

                                        filterUsers();
                                    }

                                    toggleUserDialogOpen();
                                }}
                            >
                                {userDialogMode === DialogMode.update ? 'Apply' : 'Create'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                :
                <></>
        }
    </>;
}
